import axios, { AxiosInstance } from 'axios'
import {AuthModel} from './_models'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
let isRefreshing = false
let refreshTokenPromise: Promise<AuthModel | undefined> | null = null
const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {

      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }
  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }
  
  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

const refreshAuthToken = async (api:AxiosInstance): Promise<any | undefined> => {
    if (!refreshTokenPromise) {
      isRefreshing = true
      refreshTokenPromise = new Promise(async (resolve, reject) => {
        try {
          const response = await api.post('/auth/refresh-token', {
            refreshToken: getAuth()?.refreshToken,
          })
          const newAuth = response?.data?.data
          setAuth(newAuth)
          isRefreshing = false
          refreshTokenPromise = null
          resolve(newAuth)
        } catch (error:any) {
          console.error('Token refresh failed', error)    
          if (error.response?.status === 401) {
            window.location.href = '/login'
            removeAuth()
          }
          isRefreshing = false
          refreshTokenPromise = null
          reject(error)
        }
      })
    }
    return refreshTokenPromise
}

export function setupAxios(api: any) {
  api.defaults.headers.Accept = 'application/json'
  api.interceptors.request.use(
    (config: {headers: {Authorization: string}}) => {
      const auth = getAuth()
      if (auth && auth.accessToken) {
        config.headers.Authorization = `Bearer ${auth.accessToken}`
      }
      return config
    },
    (err: any) => Promise.reject(err)
  )
  
  api.interceptors.response.use(
    (response: any) => response,
    async (error:any) => {
      const originalRequest = error.config
      const status = error.response?.status

      if (status === 408) {
        // Token has expired, attempt to refresh it
        const newAuth = await refreshAuthToken(api)
      
        if (newAuth?.accessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAuth.accessToken}`
          return api(originalRequest) // Retry original request with new token
        }
      }
       else if (status === 401) {
        window.location.href = '/auth'
        removeAuth()
      }
      return Promise.reject(error)
    }
  )
}

export {getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY}
